import './dropdown.scss'

import {Mod} from 'campagne/types'
import Downshift, {ControllerStateAndHelpers} from 'downshift'
import {Field} from 'formik'
import React, {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useState
} from 'react'
import {CrossIcon, DropdownArrowIcon, ErrorIcon} from 'styles/inline-icons'
import {style} from 'util/campagne/style'

export type DropdownType<T> = {
	// items: Array<{[key: string]: string}>
	mod?: Mod<'green_arrow'>
	name: string
	placeholder: string
	autocomplete?: boolean
	keepQuery?: boolean
	itemToString: (item: T) => string
	itemToKey: (item: T) => void
	children?: ReactNode | ((query: string) => ReactNode)
}

const styles = style('dropdown')

const DropDownContext = createContext<ControllerStateAndHelpers<any>>(null)

export const DropdownItem = ({
	children,
	item,
	small = false,
	index,
	...props
}) => {
	const {highlightedIndex, getItemProps} = useContext(DropDownContext)
	return (
		<li
			class={styles('container-items-item').mod({small})}
			{...getItemProps({
				key: item.value,
				index,
				item,
				...props
			})}
			style={{
				backgroundColor: highlightedIndex === index ? '#d5dde0' : 'white'
			}}
		>
			{children}
		</li>
	)
}

function keepAutocompleteQuery(state, changes) {
	if (changes.inputValue === null)
		return {...changes, inputValue: state.inputValue}
	return changes
}

const SyncQueryAndInput = ({query, set}) => {
	useEffect(() => set(query), [query])
	return null
}

export const Dropdown = <T extends {}>({
	name,
	placeholder,
	autocomplete,
	keepQuery,
	mod,
	itemToString,
	itemToKey,
	children
}: DropdownType<T>) => {
	const [isFocused, setFocused] = useState(false)
	return (
		<Field type="text" name={name}>
			{({form, meta}) => (
				<Downshift
					onChange={(item, state) => {
						form.setFieldValue(name, itemToKey(item))
					}}
					itemToString={itemToString}
					stateReducer={keepQuery ? keepAutocompleteQuery : undefined}
				>
					{downshift => {
						const {
							getMenuProps,
							getInputProps,
							isOpen,
							selectedItem,
							getToggleButtonProps,
							itemToString: asitemToString,
							inputValue,
							clearSelection
						} = downshift
						const hasError = meta.error && !isFocused
						return (
							<div class={styles.is({open: isOpen}).mod(mod)}>
								{autocomplete ? (
									<div class={styles('label').is({error: hasError})}>
										{keepQuery && (
											<SyncQueryAndInput
												query={inputValue}
												set={query => form.setFieldValue(name, query)}
											/>
										)}
										<input
											{...getInputProps()}
											className={styles('label-value')}
											placeholder={placeholder}
										/>
										<ErrorIcon
											className={styles('label-erroricon')
												.is({visible: hasError})
												.toString()}
										/>
										<span
											class={styles('label-crosscontainer')}
											onClick={() => clearSelection()}
										>
											<CrossIcon
												className={styles('label-crosscontainer-cross')
													.is({visible: inputValue && inputValue.length > 0})
													.toString()}
											/>
										</span>
									</div>
								) : (
									<button
										{...getToggleButtonProps()}
										class={styles('label').is({error: hasError})}
									>
										<span className={styles('label-placeholder')}>
											{selectedItem
												? asitemToString(selectedItem)
												: placeholder}
										</span>
										<ErrorIcon
											className={styles('label-erroricon')
												.is({visible: hasError})
												.toString()}
										/>
										<DropdownArrowIcon
											className={styles('label-arrow')
												.is({open: isOpen})
												.toString()}
										/>
									</button>
								)}
								<ul
									class={styles('container').is({open: isOpen})}
									{...getMenuProps()}
								>
									<div class={styles('container-items')}>
										<DropDownContext.Provider value={downshift}>
											{autocomplete && typeof children === 'function'
												? children(inputValue)
												: children}
										</DropDownContext.Provider>
									</div>
								</ul>
							</div>
						)
					}}
				</Downshift>
			)}
		</Field>
	)
}
